import React from "react";
import { useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Product } from "@dashboard/products/types";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { PrimaryButton, SecondaryButton, SplitButton } from "@shared/CustomButton";
import { Modal } from "@shared/Modal/Modal";
import { ProgressBar } from "@shared/ProgressBar";
import { Tooltip } from "@shared/Tooltip";

import { getBuildButtonTooltip, getImportButtonTooltip, getSplitButtonTooltip, useImportFile } from "./MainButton.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    buttonBox: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
}));

const ImportModal = (props: {
    name: string | null;
    progress: number;
    speed: string;
    currentFileIndex: number;
    totalFiles: number;
    onAbort: () => void;
}) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "modals"]);
    return (
        <Modal
            open={!!props.name}
            title={t("modals:uploadingFiles.title")}
            endButton={<SecondaryButton onClick={props.onAbort}>{t("common:button.cancel")}</SecondaryButton>}
        >
            <Box className={classes.content}>
                <Typography variant="body1">{props.name}</Typography>
                <ProgressBar progress={props.progress} color={theme.palette.emteria.primary} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">{props.speed}</Typography>
                    {props.totalFiles > 1 && (
                        <Typography variant="body1" color="textSecondary">
                            {t("modals:uploadingFiles.fileCount", { count: props.totalFiles })}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export type Props = {
    product: Product;
    isLoading: boolean;
    cannotManageProducts: boolean;
    isExpired: boolean;
    isBuildLimitReached: boolean;
    error: string | null;
};
export const MainButton = (props: Props) => {
    const importInput = useImportFile(props.product);

    const form = useForm();
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "pages"]);

    const mayBuild = props.product.actionFeatures?.includes("Build");
    const mayImport = props.product.actionFeatures?.includes("Import");

    const { title: buildButtonTitle, disabled: buildButtonDisabled } = getBuildButtonTooltip(props, mayBuild);
    const { title: importButtonTitle, disabled: importButtonDisabled } = getImportButtonTooltip(props, mayImport);
    const { title: splitButtonTitle, disabled: splitButtonDisabled } = getSplitButtonTooltip(props, mayBuild, mayImport);

    const onBuild = async () => {
        await form.submit();
    };
    const onImport = () => {
        importInput.ref.current?.click();
    };
    const onSubmit = async (value: "Import" | "Build") => {
        if (value === "Import") {
            onImport();
        }
        if (value === "Build") {
            await onBuild();
        }
    };

    if (mayBuild && mayImport) {
        return (
            <Box className={classes.buttonBox}>
                <input type="file" ref={importInput.ref} style={{ display: "none" }} onChange={importInput.onChange} />
                <SplitButton
                    disabled={splitButtonDisabled}
                    loading={props.isLoading || importInput.isUploading}
                    onLeftClick={onSubmit}
                    tooltipProps={{
                        title: splitButtonTitle,
                        hide: !splitButtonDisabled,
                    }}
                    menuOptions={[
                        { label: t("common:button.import"), value: "Import" },
                        { label: t("common:button.build"), value: "Build" },
                    ]}
                />
                <Tooltip title={t("pages:productPage.tooltip.toImportBuild")}>
                    <i className="fas fa-info-circle" />
                </Tooltip>
                <ImportModal
                    name={importInput.name}
                    progress={importInput.progress}
                    speed={importInput.speed}
                    currentFileIndex={importInput.currentFileIndex}
                    totalFiles={importInput.totalFiles}
                    onAbort={importInput.onAbort}
                />
            </Box>
        );
    }

    if (mayBuild) {
        return (
            <PrimaryButton
                onClick={onBuild}
                disabled={buildButtonDisabled}
                loading={props.isLoading}
                tooltipProps={{
                    title: buildButtonTitle,
                    hide: !buildButtonDisabled,
                }}
            >
                {t("common:button.build")}
            </PrimaryButton>
        );
    }

    if (mayImport) {
        return (
            <Box className={classes.buttonBox}>
                <input type="file" ref={importInput.ref} style={{ display: "none" }} onChange={importInput.onChange} />
                <Tooltip title={importButtonTitle}>
                    <PrimaryButton
                        onClick={onImport}
                        disabled={importButtonDisabled}
                        loading={props.isLoading || importInput.isUploading}
                    >
                        {t("common:button.import")}
                    </PrimaryButton>
                </Tooltip>
                <Tooltip title={t("pages:productPage.tooltip.toImportBuild")}>
                    <i className="fas fa-info-circle" />
                </Tooltip>
                <ImportModal
                    name={importInput.name}
                    progress={importInput.progress}
                    speed={importInput.speed}
                    currentFileIndex={importInput.currentFileIndex}
                    totalFiles={importInput.totalFiles}
                    onAbort={importInput.onAbort}
                />
            </Box>
        );
    }

    return (
        <Tooltip title={t("pages:productPage.tooltip.addBuildActions")}>
            <PrimaryButton disabled>{t("common:button.build")}</PrimaryButton>
        </Tooltip>
    );
};
